import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { Highlight } from "prism-react-renderer";
import {
  CheckCircle2,
  LockKeyhole,
  RefreshCw,
} from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";
import "./AssayLanguageCompact.css";

const promptText =
  "Run ELISA for 96 samples with HRP secondary, 50µL per well";

const generatedMethod = `from cornucopia import custom_labware

plate = custom_labware(
    name="elisa_plate",
    format="96_well",
    wells="A1:H12",
)

method = {
    "secondary": "HRP",
    "volume_ul": 50,
}`;

const codeThemes = {
  light: {
    plain: { color: "#334155", backgroundColor: "transparent" },
    styles: [
      { types: ["keyword", "function", "class-name"], style: { color: "#0461C2" } },
      { types: ["string"], style: { color: "#475569" } },
      { types: ["number", "boolean"], style: { color: "#034DA0" } },
      { types: ["punctuation", "operator"], style: { color: "#64748B" } },
    ],
  },
  dark: {
    plain: { color: "#CBD5E1", backgroundColor: "transparent" },
    styles: [
      { types: ["keyword", "function", "class-name"], style: { color: "#38A3FF" } },
      { types: ["string"], style: { color: "#CBD5E1" } },
      { types: ["number", "boolean"], style: { color: "#38A3FF" } },
      { types: ["punctuation", "operator"], style: { color: "#94A3B8" } },
    ],
  },
};

const animationStages = {
  idle: { label: "Ready", approved: false },
  typing: { label: "Capturing request", approved: false },
  code: { label: "Method ready", approved: false },
  robot: { label: "Running", approved: true },
  done: { label: "Run recorded", approved: true },
};

const Typewriter = ({ active, text: finalText, onComplete }) => {
  const reducedMotion = useReducedMotion();
  const [text, setText] = useState(reducedMotion ? finalText : "");
  const [cursorVisible, setCursorVisible] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!active) {
      setText(reducedMotion ? finalText : "");
      setCursorVisible(false);
      return undefined;
    }

    if (reducedMotion) {
      setText(finalText);
      setCursorVisible(false);
      onCompleteRef.current?.();
      return undefined;
    }

    let cancelled = false;
    let characterIndex = 0;
    let typingTimer;
    setText("");
    setCursorVisible(true);

    const typeNextCharacter = () => {
      if (cancelled) return;
      characterIndex += 1;
      setText(finalText.slice(0, characterIndex));

      if (characterIndex < finalText.length) {
        typingTimer = window.setTimeout(typeNextCharacter, 24);
        return;
      }

      setCursorVisible(false);
      onCompleteRef.current?.();
    };

    typingTimer = window.setTimeout(typeNextCharacter, 240);

    return () => {
      cancelled = true;
      window.clearTimeout(typingTimer);
    };
  }, [active, finalText, reducedMotion]);

  return (
    <>
      <span
        className="font-mono text-xs leading-5 text-slate-800 dark:text-white/[0.85]"
        aria-hidden="true"
      >
        {text}
        {cursorVisible && <span className="ml-0.5 text-primary">|</span>}
      </span>
      <span className="sr-only">{finalText}</span>
    </>
  );
};

const Plate = ({ active, darkMode, onComplete }) => {
  const reducedMotion = useReducedMotion();
  const [filled, setFilled] = useState(new Set());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const wellCount = 96;
  const rowCount = 8;
  const columnCount = 12;
  const wellSize = 6;
  const gap = 1.5;

  useEffect(() => {
    if (!active) {
      setFilled(new Set());
      return undefined;
    }

    if (reducedMotion) {
      setFilled(new Set(Array.from({ length: wellCount }, (_, index) => index)));
      onCompleteRef.current?.();
      return undefined;
    }

    let currentWell = 0;
    let fillTimer;
    let completionTimer;
    const startTimer = window.setTimeout(() => {
      fillTimer = window.setInterval(() => {
        const nextWell = currentWell;
        currentWell += 1;
        setFilled((previous) => new Set([...previous, nextWell]));

        if (currentWell >= wellCount) {
          window.clearInterval(fillTimer);
          completionTimer = window.setTimeout(
            () => onCompleteRef.current?.(),
            220
          );
        }
      }, 18);
    }, 220);

    return () => {
      window.clearTimeout(startTimer);
      window.clearInterval(fillTimer);
      window.clearTimeout(completionTimer);
    };
  }, [active, reducedMotion]);

  const width = columnCount * (wellSize + gap) - gap;
  const height = rowCount * (wellSize + gap) - gap;

  return (
    <div className="flex w-full flex-col items-center">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-[280px]"
        role="img"
        aria-label={`${filled.size} of 96 wells complete`}
      >
        {Array.from({ length: wellCount }, (_, index) => {
          const x = (index % columnCount) * (wellSize + gap) + wellSize / 2;
          const y = Math.floor(index / columnCount) * (wellSize + gap) + wellSize / 2;
          const isFilled = filled.has(index);

          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r={wellSize / 2 - 0.5}
              fill={
                isFilled
                  ? darkMode
                    ? "#38A3FF"
                    : "#0575E6"
                  : darkMode
                    ? "#202632"
                    : "#DCE7F2"
              }
              opacity={isFilled ? 0.92 : 0.82}
              className="transition-colors duration-150"
            />
          );
        })}
      </svg>
      <div className="mt-5 w-full max-w-[280px]">
        <div className="h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-white/[0.08]">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-150"
            style={{ width: `${(filled.size / wellCount) * 100}%` }}
          />
        </div>
        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-slate-600 dark:text-white/[0.65]">
          {filled.size}/96 wells
        </p>
      </div>
    </div>
  );
};

const AssayLanguageDemo = ({ compact = false }) => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.22 });
  const reducedMotion = useReducedMotion();
  const { darkMode = false } = useContext(ThemeContext) || {};
  const [runKey, setRunKey] = useState(0);
  const [stage, setStage] = useState(reducedMotion ? "done" : "idle");

  useEffect(() => {
    if (!inView) return;
    setStage(reducedMotion ? "done" : "typing");
  }, [inView, reducedMotion, runKey]);

  const handleTypingComplete = useCallback(() => {
    setStage(reducedMotion ? "done" : "code");
  }, [reducedMotion]);

  useEffect(() => {
    if (stage !== "code" || reducedMotion) return undefined;
    const runTimer = window.setTimeout(() => setStage("robot"), 1150);
    return () => window.clearTimeout(runTimer);
  }, [stage, reducedMotion]);

  const currentStage = animationStages[stage];
  const codeTheme = darkMode ? codeThemes.dark : codeThemes.light;
  const promptReady = stage !== "idle" && stage !== "typing";
  const codeReady = stage === "code" || stage === "robot" || stage === "done";
  const executionActive = stage === "robot" || stage === "done";

  return (
    <section
      id="automate-assay-story"
      ref={sectionRef}
      data-primary-nav="solutions"
      className={`assay-language-section section-anchor relative overflow-hidden border-b border-slate-200 px-6 py-24 dark:border-white/[0.08] sm:py-32${compact ? " assay-language-compact" : ""}`}
      aria-labelledby="assay-language-title"
    >
      {!compact && <div className="assay-language-grid pointer-events-none absolute inset-0" aria-hidden="true" />}
      <div className="assay-language-shell wide-shell-xl relative mx-auto max-w-7xl">
        {compact ? (
          <div className="assay-language-intro">
            <h3 id="assay-language-title">From instruction to experiment.</h3>
            <p>A working SOP becomes a draft method, reviewed by a scientist before the run.</p>
          </div>
        ) : <div className="grid gap-10 lg:grid-cols-[0.37fr_0.63fr] lg:gap-20">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
              Assay onboarding
            </p>
          </div>
          <div>
            <h2
              id="assay-language-title"
              className="max-w-5xl font-serif text-5xl font-normal leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-8xl"
            >
              Ask for a run <span className="text-primary">in plain language.</span>
            </h2>
            <p className="mt-7 max-w-3xl text-lg leading-8 text-slate-600 dark:text-white/[0.70]">
              Once we have onboarded a working SOP, a scientist can request a
              run in plain language. The platform drafts the method; a scientist
              or automation engineer reviews it and starts the run before
              anything moves.
            </p>
          </div>
        </div>}

        <div className="assay-language-interface infra-control-plane mt-16">
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {currentStage.label}{stage === "done" ? ". 96 of 96 wells complete." : "."}
          </p>
          {!compact && <div className="h-1 bg-gradient-to-r from-primary via-primary to-primary-400" aria-hidden="true" />}

          <div className="assay-language-toolbar flex min-h-14 flex-col items-start justify-between gap-3 border-b border-slate-200 px-5 py-3 dark:border-white/[0.1] sm:flex-row sm:items-center sm:gap-4 sm:px-7">
            <div className="flex items-center gap-1.5">
              {!compact && [0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/[0.12]"
                  aria-hidden="true"
                />
              ))}
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-600 dark:text-white/[0.65]">
                Cornucopia / assay SDK
              </span>
            </div>
            <span
              className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] ${
                currentStage.approved
                  ? "border-primary/20 bg-primary/[0.06] text-primary-700 dark:border-primary/30 dark:bg-primary/[0.08] dark:text-primary-400"
                  : "border-amber-500/25 bg-amber-50 text-amber-700 dark:border-amber-300/30 dark:bg-amber-300/[0.06] dark:text-amber-200"
              }`}
            >
              {currentStage.approved ? (
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {currentStage.approved ? "Scientist approved" : "Scientist review"}
            </span>
          </div>

          <div className="assay-language-request border-b border-slate-200 bg-slate-50/65 px-5 py-5 dark:border-white/[0.1] dark:bg-white/[0.018] sm:px-7">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 dark:text-white/[0.65]">
              Natural-language request
            </p>
            <div className="min-h-14 rounded-xl border border-slate-200 bg-white px-4 py-4 dark:border-white/[0.08] dark:bg-white/[0.04]">
              {stage === "typing" ? (
                <Typewriter
                  key={`type-${runKey}`}
                  active
                  text={promptText}
                  onComplete={handleTypingComplete}
                />
              ) : (
                <span className="font-mono text-xs leading-5 text-slate-800 dark:text-white/[0.85]">
                  {promptReady ? promptText : "Waiting for an instruction…"}
                </span>
              )}
            </div>
          </div>

          <div className="assay-language-workflow grid border-b border-slate-200 dark:border-white/[0.1] lg:grid-cols-[1.16fr_0.84fr]">
            <div className="assay-language-method min-w-0 border-b border-slate-200 p-5 dark:border-white/[0.1] sm:p-7 lg:border-b-0 lg:border-r">
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 dark:text-white/[0.65]">
                  Generated method
                </p>
                <span className="font-mono text-[10px] text-primary-700 dark:text-primary-400">
                  Python method
                </span>
              </div>
              <div className="assay-language-code min-h-[300px] overflow-hidden rounded-xl border border-slate-200 bg-[#f8fbff] dark:border-white/[0.07] dark:bg-[#070a10]">
                <AnimatePresence mode="wait" initial={false}>
                  {!codeReady ? (
                    <motion.div
                      key="waiting-for-input"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="assay-language-waiting flex min-h-[300px] items-center justify-center px-6 text-center"
                    >
                      <p className="font-mono text-[11px] leading-5 text-slate-600 dark:text-white/60">
                        Waiting for the complete instruction…
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="generated-code"
                      initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: reducedMotion ? 0 : 0.3 }}
                    >
                      <Highlight theme={codeTheme} code={generatedMethod} language="python">
                        {({ className, style, tokens, getLineProps, getTokenProps }) => (
                          <pre
                            className={`${className} overflow-x-auto p-5 font-mono text-[11px] leading-[1.65]`}
                            style={{ ...style, background: "transparent", margin: 0 }}
                          >
                            {tokens.map((line, lineIndex) => (
                              <motion.div
                                key={lineIndex}
                                {...getLineProps({ line })}
                                className="flex min-w-max"
                                initial={reducedMotion ? false : { opacity: 0, x: -4 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  duration: reducedMotion ? 0 : 0.2,
                                  delay: reducedMotion ? 0 : lineIndex * 0.03,
                                }}
                              >
                                <span className="mr-3 w-4 shrink-0 select-none text-right text-slate-300 dark:text-white/20">
                                  {String(lineIndex + 1).padStart(2, "0")}
                                </span>
                                <span>
                                  {line.map((token, tokenIndex) => (
                                    <span key={tokenIndex} {...getTokenProps({ token })} />
                                  ))}
                                </span>
                              </motion.div>
                            ))}
                          </pre>
                        )}
                      </Highlight>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="assay-language-execution flex min-h-[390px] min-w-0 flex-col bg-white p-5 dark:bg-[#0a0c12] sm:p-7">
              <div className="mb-7 flex items-center justify-between gap-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 dark:text-white/[0.65]">
                  Execution
                </p>
                <span
                  className={`inline-flex items-center gap-2 font-mono text-[10px] ${
                    executionActive
                      ? "text-primary-700 dark:text-primary-400"
                      : "text-slate-600 dark:text-white/60"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      executionActive ? "bg-primary" : "bg-slate-300 dark:bg-white/20"
                    }`}
                    aria-hidden="true"
                  />
                  {currentStage.label}
                </span>
              </div>

              <div className="flex flex-1 items-center justify-center py-4">
                <Plate
                  key={`plate-${runKey}`}
                  active={executionActive}
                  darkMode={darkMode}
                  onComplete={() => setStage("done")}
                />
              </div>

              <dl className="mt-6 space-y-2 border-t border-slate-200 pt-5 font-mono text-[10px] dark:border-white/[0.08]">
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
                  <dt className="text-slate-600 dark:text-white/60">Hardware</dt>
                  <dd className="min-w-0 break-words text-right text-slate-700 [overflow-wrap:anywhere] dark:text-white/75">
                    Hamilton Microlab STAR V
                  </dd>
                </div>
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
                  <dt className="text-slate-600 dark:text-white/60">Format</dt>
                  <dd className="min-w-0 break-words text-right text-slate-700 [overflow-wrap:anywhere] dark:text-white/75">
                    96-well plate
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="assay-language-caption flex flex-col gap-4 border-l-2 border-l-primary/35 bg-primary/[0.035] px-5 py-4 dark:border-l-primary/55 dark:bg-primary/[0.055] sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <p className="max-w-3xl font-mono text-[10px] leading-5 tracking-[0.03em] text-slate-600 dark:text-white/[0.65]">
              Illustrative interface. The generated method is reviewed and tested
              before an approved run begins.
            </p>
            {!reducedMotion && (
              <button
                type="button"
                onClick={() => {
                  setStage("typing");
                  setRunKey((current) => current + 1);
                }}
                className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 text-sm font-semibold text-slate-800 outline-none transition-colors hover:border-primary hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:border-white/[0.14] dark:text-white/75 dark:hover:border-primary-400 dark:hover:text-primary-400"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Replay sequence
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssayLanguageDemo;
